import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { toast } from '../../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../../constants/DialogTypes';
import { Button, DataTable, Dialog, NameEmailAvatar } from '../../../../../ui';
import { Tabs } from '../../../../../ui/components/Common/Tabs/Tabs';
import { Refresh } from '../../../../../ui/icons/icons';
import { UploadFileComponent } from '../../../../../ui/input/FileInput';
import TopBanner from '../../../../../ui/TopBanner';
import { apiUrl } from '../../../../../utils/api_service/api_url';
import { getJobApplicants, sendInviteApplicants } from '../../../../../utils/api_service/JobOpenings';
import { JobOpeningsContext } from '../../../../../utils/contexts/JobOpening';
import { useUploadForm } from '../../../../../utils/helpers/api_utils';
import { formatUnixPretty } from '../../../../../utils/helpers/time';
import {
  JobApplicant,
  JobDescription,
  JobRequisition,
} from '../../../../../utils/types/JobOpenings';
import { TooltipWrapper } from '../../../view/Functions';
import { DocumentChecklist } from '../../../viewJobPosting/JobPostDetails/components/DocumentChecklist';
import InviteListModal from './InviteList/InviteListModal';

type ApplicantStatus = {
  Completed: number;
  InProgress: number;
  Error: number;
};

export interface CVIngestionProps {
  completedElement: () => void;
  setStages: (value: any) => void;
  jobRequisition: JobRequisition;
  jobDescription: JobDescription;
}

export const CVIngestionTab: React.FC<CVIngestionProps> = ({
  completedElement,
  setStages,
  jobDescription,
}) => {
  const { id } = useParams<{ id: string }>();
  const { completedJobOpeningDetails } = useContext(JobOpeningsContext);
  const uploadURL = apiUrl + `/job-openings/${id}/cv-ingestion/file`;
  const { uploadStatus, uploadForm, progress } = useUploadForm(uploadURL);
  const [uploadedFile, setUploadedFile] = useState<File[] | null>(null);
  const [modalState, setModalState] = useState<boolean>(false);
  const [jobApplicants, setJobApplicants] = useState<JobApplicant[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [applicantStatus, setApplicantStatus] = useState<ApplicantStatus>({
    Completed: 0,
    InProgress: 0,
    Error: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [inviteLoading, setInviteLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] =
    useState<'Bulk Upload' | 'Single Candidate Upload'>('Bulk Upload');
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);

  const isResume = (key: string) => key === 'resume';

  const mandatoryDocs =
    jobDescription?.documentRequirements?.filter(
      (req) => req.isMandatory && !isResume(req.key),
    ) ?? [];
  const hasExtraDocs = (jobDescription?.documentRequirements?.length ?? 0) > 1;

  const showTabs = false;
  const forceSingleUpload = hasExtraDocs;

  useEffect(() => {
    if (forceSingleUpload) {
      setActiveTab('Single Candidate Upload');
    } else {
      setActiveTab('Bulk Upload');
    }
  }, [forceSingleUpload]);

  const [singleCandidateFiles, setSingleCandidateFiles] = useState<
    Record<string, File[]>
  >({});

  const canSubmit = useMemo(() => {
    if (uploadStatus === 'loading') return false;
    if (activeTab === 'Bulk Upload') {
      return uploadedFile && uploadedFile.length > 0;
    } else {
      const hasResume =
        jobDescription?.documentRequirements?.some(
          (req) =>
            isResume(req.key) && (singleCandidateFiles[req.key]?.length ?? 0) > 0,
        ) ?? false;
      const hasAllMandatory =
        mandatoryDocs?.every(
          (doc: any) => (singleCandidateFiles[doc.key]?.length ?? 0) > 0,
        ) ?? false;
      return hasResume && hasAllMandatory;
    }
  }, [
    activeTab,
    uploadedFile,
    singleCandidateFiles,
    mandatoryDocs,
    uploadStatus,
    jobDescription,
  ]);

  const getAllowMultiple = (type: string): boolean => {
    const req = jobDescription?.documentRequirements?.find(
      (r) => r.key === type,
    );
    return req?.allowMultiple ?? false;
  };

  const handleFileSelect = (typeOrKey: string, file: File | null) => {
    setSingleCandidateFiles((prev) => {
      const newFiles = { ...prev };
      if (file) {
        const canMultiple = getAllowMultiple(typeOrKey);
        if (canMultiple) {
          newFiles[typeOrKey] = [...(prev[typeOrKey] || []), file];
        } else {
          newFiles[typeOrKey] = [file];
        }
      } else {
        delete newFiles[typeOrKey];
      }
      return newFiles;
    });
  };

  const handleFileRemoveAtIndex = (type: string, index: number) => {
    setSingleCandidateFiles((prev) => {
      const files = prev[type] || [];
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      if (updatedFiles.length === 0) {
        const newState = { ...prev };
        delete newState[type];
        return newState;
      }
      return { ...prev, [type]: updatedFiles };
    });
  };

  const handleBatchFileSelect = (updates: { [type: string]: File | null }) => {
    setSingleCandidateFiles((prev) => {
      const newFiles = { ...prev };
      Object.entries(updates).forEach(([type, file]) => {
        if (file) {
          const canMultiple = getAllowMultiple(type);
          if (canMultiple) {
            newFiles[type] = [...(prev[type] || []), file];
          } else {
            newFiles[type] = [file];
          }
        } else {
          delete newFiles[type];
        }
      });
      return newFiles;
    });
  };

  useEffect(() => {
    fetchJobApplicants();
  }, [id, pageNumber]);

  const fetchJobApplicants = () => {
    setLoading(true);
    getJobApplicants(id, '', pageNumber, 10, '')
      .then((response) => {
        if (!response.ok) {
          throw new Error();
        }
        return response.json();
      })
      .then((data) => {
        setJobApplicants(data.jobApplicants);
        setTotalPages(Math.ceil(data.totalApplicants / 10));
        setApplicantStatus({
          Completed: data.completedApplicants,
          InProgress: data.inProgressApplicants,
          Error: data.errorApplicants,
        });
      })
      .catch(() => {
        toast.error('Error Getting Job Applicants', {
          position: 'bottom-center',
        });
        setTotalPages(1);
        setJobApplicants([]);
      })
      .finally(() => setLoading(false));
  };

  const uploadFiles = async () => {
    try {
      const formData = new FormData();
      let hasFiles = false;

      if (activeTab === 'Bulk Upload') {
        if (uploadedFile && uploadedFile.length > 0) {
          uploadedFile.forEach((file) => {
            formData.append('files', file);
          });
          hasFiles = true;
        }
      } else {
        Object.entries(singleCandidateFiles).forEach(([key, files]) => {
          if (files && files.length > 0) {
            files.forEach((file) => {
              formData.append(key, file);
            });
            hasFiles = true;
          }
        });
      }

      if (hasFiles) {
        const targetUrl =
          activeTab === 'Bulk Upload'
            ? uploadURL
            : apiUrl + `/job-openings/${id}/document/upload`;

        await uploadForm(formData, targetUrl);
        toast.success('Successfully Uploaded Files', {
          position: 'bottom-center',
        });
        completedElement();
        setStages((prevStages: any[]) =>
          prevStages.map((stage) =>
            stage.label === 'Shortlisting'
              ? { ...stage, status: 'In Progress' }
              : stage,
          ),
        );
        setUploadedFile(null);
        setSingleCandidateFiles({});
        setModalState(false);
        fetchJobApplicants();
      }
    } catch {
      toast.error('Failed Uploading Files', { position: 'bottom-center' });
    }
  };

  const handleCloseModal = () => {
    setModalState(false);
    setUploadedFile(null);
    setSingleCandidateFiles({});
  };

  const refreshApplicants = () => {
    setPageNumber(1);
    fetchJobApplicants();
    setSelectedApplicants([]);
  };

  const handleSelectRow = (row: JobApplicant) => {
    const applicantId = row.id;
    if (!applicantId) return;
    setSelectedApplicants((prev) =>
      prev.includes(applicantId)
        ? prev.filter((id) => id !== applicantId)
        : [...prev, applicantId],
    );
  };

  const handleSelectAll = () => {
    if (selectedApplicants.length === jobApplicants.length) {
      setSelectedApplicants([]);
    } else {
      const allIds = jobApplicants
        .map((a) => a.id)
        .filter((i): i is string => !!i);
      setSelectedApplicants(allIds);
    }
  };

  const handleSendInvite = async () => {
    if (selectedApplicants.length === 0) return;
    setInviteLoading(true);
    try {
      const response = await sendInviteApplicants(id, selectedApplicants);
      if (response.ok) {
        toast.success(
          `Successfully sent invites to ${selectedApplicants.length} applicants`,
          {
            position: 'bottom-center',
          },
        );
        setSelectedApplicants([]);
        fetchJobApplicants();
      } else {
        throw new Error();
      }
    } catch {
      toast.error('Failed to send invites', { position: 'bottom-center' });
    } finally {
      setInviteLoading(false);
    }
  };

  const columns = [
    {
      key: 'baseFilename',
      label: 'Filename',
      align: 'left' as const,
      render: (row: any) => <TooltipWrapper text={row.baseFilename} />,
    },
    {
      key: 'documentType',
      label: 'Document Type',
      align: 'left' as const,
      render: (row: any) => <TooltipWrapper text={row.documentType} />,
    },
    {
      key: 'details',
      label: 'Applicant Details',
      align: 'left' as const,
      render: (row: JobApplicant) => (
        <NameEmailAvatar
          name={(row.firstName ?? '') + ' ' + (row.lastName ?? '')}
          email={row.email ?? ''}
          avatar={row.base64Encoded}
        />
      ),
    },
    {
      key: 'createdAt',
      label: 'Date Uploaded',
      align: 'left' as const,
      render: (row: any) => (
        <TooltipWrapper text={formatUnixPretty(row.createdAt as number)} />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left' as const,
      render: (row: JobApplicant) => (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
          {row.status === 'Error' ? (
            <>
              <Chip
                label='Error'
                size='small'
                color='error'
                sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20, width: 'fit-content' }}
              />
              {row.errorMessage && (
                <Typography
                  variant='caption'
                  title={row.errorMessage}
                  noWrap
                  sx={{ color: 'error.main', maxWidth: 120, fontSize: '0.65rem', mt: 0.5 }}
                >
                  {row.errorMessage}
                </Typography>
              )}
            </>
          ) : (
            <Chip
              label={row.status}
              size='small'
              color={row.status === 'Completed' || row.status === 'Success' ? 'success' : 'warning'}
              variant='outlined'
              sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20, width: 'fit-content' }}
            />
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 2, height: '100%' }}>
      {!completedJobOpeningDetails.includes('CV Ingestion') && (
        <TopBanner
          title='Final Check: Confirm Before Uploading'
          message='Once you upload the first applicant, all configurations tabs will be disabled, and their values will be locked. Please ensure all information is accurate before proceeding.'
          type='warning'
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { lg: 'center' }, justifyContent: { lg: 'space-between' }, gap: 2 }}>
        {/* Status counters */}
        <Grid container spacing={1} sx={{ maxWidth: { lg: 380 } }}>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.light',
                bgcolor: 'success.light',
              }}
            >
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', color: 'success.dark', fontSize: '0.65rem' }}>
                Completed
              </Typography>
              <Typography variant='h6' sx={{ fontWeight: 800, color: 'success.dark', lineHeight: 1.2 }}>
                {applicantStatus.Completed}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.light',
                bgcolor: 'info.light',
              }}
            >
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', color: 'info.dark', fontSize: '0.65rem' }}>
                Progress
              </Typography>
              <Typography variant='h6' sx={{ fontWeight: 800, color: 'info.dark', lineHeight: 1.2 }}>
                {applicantStatus.InProgress}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.light',
                bgcolor: 'error.light',
              }}
            >
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', color: 'error.dark', fontSize: '0.65rem' }}>
                Error
              </Typography>
              <Typography variant='h6' sx={{ fontWeight: 800, color: 'error.dark', lineHeight: 1.2 }}>
                {applicantStatus.Error}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {selectedApplicants.length > 0 && (
            <Button
              onClick={handleSendInvite}
              label={
                inviteLoading
                  ? 'Sending...'
                  : `Send Invite (${selectedApplicants.length})`
              }
              variant='primary'
              sx={{ width: { xs: '100%', sm: 'auto' }, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, border: 'none', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.2)' }}
              disabled={inviteLoading}
            />
          )}
          <Button
            onClick={() => setIsInviteModalOpen(true)}
            label='Candibank Invites'
            variant='secondary'
            sx={{ width: { xs: '100%', sm: 'auto' }, color: '#047857', borderColor: '#a7f3d0', '&:hover': { bgcolor: '#ecfdf5' }, boxShadow: 1 }}
          />
          <Button
            onClick={() => refreshApplicants()}
            label=''
            hasIcon
            icon={
              <Refresh size={24} color='#059669' />
            }
            variant='secondary'
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          />

          {showTabs ? (
            <Tabs
              tabs={[
                { label: 'Bulk Upload (Resume Only)', value: 'Bulk Upload' },
                {
                  label: 'Single Candidate Upload',
                  value: 'Single Candidate Upload',
                },
              ]}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab as any)}
            />
          ) : (
            <Button
              onClick={() => setModalState(true)}
              label={forceSingleUpload ? 'Upload Application' : 'Upload Resume'}
              variant='primary'
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          )}

          {showTabs && (
            <Button
              onClick={() => setModalState(true)}
              label={
                activeTab === 'Bulk Upload'
                  ? 'Upload Resume'
                  : 'Upload Application'
              }
              variant='primary'
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          )}
        </Box>
      </Box>

      {/* Table container */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 400, overflow: 'hidden' }}>
        <DataTable
          data={jobApplicants}
          columns={columns}
          isLoading={loading}
          currentPage={pageNumber}
          totalPages={totalPages}
          totalRecords={jobApplicants.length * totalPages}
          onPageChange={(page) => setPageNumber(page)}
          rowsPerPage={10}
          hasCheckBox
          onSelectAll={handleSelectAll}
          onSelectRow={(index, row: any) => handleSelectRow(row)}
          isRowSelected={(index, row: any) =>
            selectedApplicants.includes(row.id)
          }
          isAllSelected={
            selectedApplicants.length > 0 &&
            selectedApplicants.length === jobApplicants.length
          }
          isIndeterminate={
            selectedApplicants.length > 0 &&
            selectedApplicants.length < jobApplicants.length
          }
        />
      </Box>

      {/* Upload Dialog */}
      <Dialog
        isOpen={modalState}
        onClose={handleCloseModal}
        title={
          activeTab === 'Bulk Upload' && !forceSingleUpload
            ? 'Bulk Upload Resumes'
            : 'Upload Application'
        }
        dialogType={DIALOG_TYPE.SUCCESS}
        justify='center'
        confirmButtonLable={
          uploadStatus === 'loading' ? 'Uploading...' : 'Upload'
        }
        cancelButtonLable='Cancel'
        handleApply={uploadFiles}
        disabled={!canSubmit}
        maxWidth='800px'
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            {activeTab === 'Bulk Upload' && !forceSingleUpload
              ? 'Please select one or more resume files to upload in bulk.'
              : 'Please upload the following documents requested for this position.'}
          </Typography>
          {activeTab === 'Bulk Upload' && !forceSingleUpload ? (
            <UploadFileComponent
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              validTypes={[
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              ]}
              isMultiple={true}
            />
          ) : (
            <DocumentChecklist
              documentRequirements={jobDescription?.documentRequirements ?? []}
              selectedFiles={singleCandidateFiles}
              onFileSelect={handleFileSelect}
              onBatchFileSelect={handleBatchFileSelect}
              onFileRemoveAtIndex={handleFileRemoveAtIndex}
              disabled={uploadStatus === 'loading'}
            />
          )}

          {uploadStatus === 'loading' && (
            <Box sx={{ my: 3 }}>
              <LinearProgress
                variant='determinate'
                value={progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant='caption' sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 1 }}>
                {Math.round(progress)}% Complete
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>

      <InviteListModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        jobOpeningId={id || ''}
      />
    </Box>
  );
};

export default CVIngestionTab;
